import { Brand } from "@/core/Brand";
import { Store } from "@/core/Store";
import { useRef, useState } from "react";

import { EnvironmentAPI } from "@/api/EnvironmentAPI";
import { Button } from "@/components";
import styles from "@/styles/containers/configure.module.scss";

import classNames from "classnames/bind";
import { ConfigurationFieldInput } from "./ConfigurationFieldInput";
import { ConfigurationFieldLabel } from "./ConfigurationFieldLabel";

import { Alert } from "@/components/Alert";
import { useAppConfiguration } from "@/hooks/useAppConfiguration";

import { useColibriTableConfiguration } from "@/hooks/useColibriTableConfiguration";
import Image from "next/image";

const css = classNames.bind(styles);

export default function ColibriConfiguration({
  onBack,
  onConfirm,
}) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [errorMessage, setErrorMessage] = useState(null);
  const [fieldToEdit, setFieldToEdit] = useState(null);

  const [appConfigurations, updateAppConfigurations, persistAppConfigurations] =
    useAppConfiguration();

  const [configurations, updateConfigurations, persistConfigurations, isValid] =
    useColibriTableConfiguration();

  const onOpen = async () => {
    setLoading(true);

    if (isValid) {
      persistConfigurations();
      persistAppConfigurations();

      try {
        await loadEnvironment(appConfigurations.unitCode);
      } catch (err) {
        console.error(err);
      }
    }

    setLoading(false);
  };

  const loadEnvironment = async (code) => {
    try {
      setLoading(true);

      const environmentResponse = await EnvironmentAPI.getEnvironmentByCode(
        code
      );

      const data = environmentResponse.getData({});
      const brandAlias = data.brandAlias;
      const storeAlias = data.unitAlias;

      if (environmentResponse.getErrors([]).length === 0) {
        await Brand.install(brandAlias);
        await Store.install(storeAlias);

        Brand.alias = brandAlias;
        Store.alias = storeAlias;

        onConfirm();
      } else {
        setError(true);
        setErrorMessage(`${environmentResponse.getErrors([])?.[0]}`);
      }
    } catch (err) {
      setError(true);
      setErrorMessage(`${err.message}`);
    } finally {
      setLoading(false);
    }
  };

  return (
    <>
      {!fieldToEdit && (
        <>
          <div className={css({ welcome: true, show: true })}>
            <div className={styles.logo}>
              <Image
                src="https://olga.tech/favicon-olga.png"
                alt="Olga"
                width={95}
                height={95}
              />
            </div>

            <p className={styles.title}>Boas vindas</p>

            <p className={styles.version}>
              App {process.env.NEXT_PUBLIC_VERSION}
            </p>

            
            <>
              <ConfigurationFieldLabel
                label="Código da unidade"
                value={appConfigurations.unitCode}
                onClick={() => setFieldToEdit("UNIT")}
              />

              <ConfigurationFieldLabel
                label="Host"
                value={configurations.host}
                onClick={() => setFieldToEdit("HOST")}
              />

              <ConfigurationFieldLabel
                label="Porta"
                value={configurations.port}
                onClick={() => setFieldToEdit("PORT")}
              />
            </>
  

            <ConfigurationFieldLabel
              label="Usuário"
              value={configurations.user}
              onClick={() => setFieldToEdit("USER")}
            />

            <ConfigurationFieldLabel
              label="Senha"
              value={configurations.password}
              onClick={() => setFieldToEdit("PASSWORD")}
            />

            <ConfigurationFieldLabel
              label="SKU do produto (Chamar garçom)"
              value={configurations.callWaiterSku}
              onClick={() => setFieldToEdit("CALL_WAITER_SKU")}
            />

            <ConfigurationFieldLabel
              label="Código da mesa"
              value={configurations.externalId}
              onClick={() => setFieldToEdit("TABLE")}
            />

            <ConfigurationFieldLabel
              label="Exibir proteção infantil antes de enviar o pedido"
              value={!!appConfigurations.allowChallenge === true ? "Sim" : "Não"}
              onClick={() => {
                updateAppConfigurations({ 
                  allowChallenge: !appConfigurations.allowChallenge
                });
              }}
            />

            <ConfigurationFieldLabel
              label="Enviar combo como itens avulsos"
              value={!!configurations.allowExplodeCombination === true ? "Sim" : "Não"}
              onClick={() => {
                updateConfigurations({ 
                  allowExplodeCombination: !configurations.allowExplodeCombination
                });
              }}
            />

          </div>

          <div className={styles.footer}>
            <Button design="secondary" onClick={() => onBack()}>
              Voltar
            </Button>

            <Button
              onClick={onOpen}
              loading={loading}
              disabled={loading || !(appConfigurations?.unitCode && isValid)}
            >
              Abrir
            </Button>
          </div>
        </>
      )}

      {fieldToEdit === "UNIT" && (
        <ConfigurationFieldInput
          label="Insira o código da unidade"
          value={appConfigurations.unitCode}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateAppConfigurations({ 
              unitCode: value,
              tableService: "COLIBRI"
            });
          }}
        />
      )}

      {fieldToEdit === "HOST" && (
        <ConfigurationFieldInput
          label="Insira o Host"
          value={configurations.host}
          onValidate={(value) => {
            const origin = window.location.origin;

            const isSameOrigin = String(value).startsWith(origin);
            const isLocalAddress = /^(\d{1,3}\.){3}\d{1,3}$/.test(String(value));

            return isSameOrigin || isLocalAddress;
          }}
          type="text"
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ host: value });
          }}
        />
      )}

      {fieldToEdit === "PORT" && (
        <ConfigurationFieldInput
          label="Insira a porta da API"
          value={configurations.port}
          onValidate={(value) => !!value}
          type="text"
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ port: value });
          }}
        />
      )}

      {fieldToEdit === "TABLE" && (
        <ConfigurationFieldInput
          label="Insira o código da mesa"
          value={configurations.externalId}
          onValidate={(value) => !!value}
          onCancel={() => {
            setFieldToEdit(null);
          }}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ externalId: value });
          }}
        />
      )}

      {fieldToEdit === "USER" && (
        <ConfigurationFieldInput
          label="Insira o usuário"
          type="text"
          value={configurations.user}
          onValidate={(value) => !!value}
          onCancel={() => setFieldToEdit(null)}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ user: value });
          }}
        />
      )}

      {fieldToEdit === "PASSWORD" && (
        <ConfigurationFieldInput
          label="Insira a senha"
          value={configurations.password}
          onValidate={(value) => !!value}
          onCancel={() => setFieldToEdit(null)}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({ password: value });
          }}
        />
      )}

      {fieldToEdit === "CALL_WAITER_SKU" && (
        <ConfigurationFieldInput
          label="Insira o SKU do produto que será enviado ao chamar o garçom"
          maxLength="8"
          value={configurations.callWaiterSku}
          onValidate={(value) => true}
          onCancel={() => setFieldToEdit(null)}
          onConfirm={(value) => {
            setFieldToEdit(null);
            updateConfigurations({
              callWaiterSku: value,
            });
          }}
        />
      )}

      <Alert
        title={errorMessage}
        subtitle="Não foi possível carregar os dados da unidade"
        open={!!error}
        onClose={() => setError(null)}
      />
    </>
  );
}
